import { useState, useEffect, useCallback, RefObject } from 'react';
import { FlatOrgTreeTableNode } from 'src/utils/treeNode';
import { APP_HEADER_HEIGHT, HEADER_HEIGHT, THEAD_HEIGHT } from './styled';

export interface BreadcrumbItem {
  key: string;
  name: string;
}

interface UseBreadcrumbPathOptions {
  tableRef: RefObject<HTMLDivElement>;
  data: FlatOrgTreeTableNode[];
  expandedKeys: Record<string, boolean>;
}

/**
 * Builds a flat map of all nodes with their parent keys for quick lookup
 */
const buildNodeMap = (
  nodes: FlatOrgTreeTableNode[],
  parentKey: string | null = null,
  map: Map<string, { node: FlatOrgTreeTableNode; parentKey: string | null }> = new Map(),
): Map<string, { node: FlatOrgTreeTableNode; parentKey: string | null }> => {
  for (const node of nodes) {
    map.set(node.key, { node, parentKey });
    if (node.children?.length) {
      buildNodeMap(node.children, node.key, map);
    }
  }
  return map;
};

/**
 * Gets the parent chain for a given node key (from root to immediate parent)
 */
const getParentChain = (
  nodeKey: string,
  nodeMap: Map<string, { node: FlatOrgTreeTableNode; parentKey: string | null }>,
): BreadcrumbItem[] => {
  const chain: BreadcrumbItem[] = [];
  let currentKey: string | null = nodeMap.get(nodeKey)?.parentKey ?? null;

  while (currentKey) {
    const entry = nodeMap.get(currentKey);
    if (entry) {
      chain.unshift({ key: entry.node.key, name: entry.node.data.name });
      currentKey = entry.parentKey;
    } else {
      break;
    }
  }

  return chain;
};

/**
 * Hook to track the breadcrumb path based on scroll position.
 * Returns the parent chain of the first visible row when scrolled down.
 */
export const useBreadcrumbPath = ({ tableRef, data, expandedKeys }: UseBreadcrumbPathOptions): BreadcrumbItem[] => {
  const [breadcrumbPath, setBreadcrumbPath] = useState<BreadcrumbItem[]>([]);

  const calculateBreadcrumb = useCallback(() => {
    if (!tableRef.current) return;

    const tbody = tableRef.current.querySelector('.p-treetable-tbody');
    if (!tbody) return;

    const rows = tbody.querySelectorAll('tr[data-rk]');
    if (rows.length === 0) {
      setBreadcrumbPath([]);
      return;
    }

    // Get the scroll container (main element)
    const scrollContainer = document.getElementById('main');
    if (!scrollContainer) return;

    const stickyThreshold = APP_HEADER_HEIGHT + HEADER_HEIGHT + THEAD_HEIGHT;
    const mainRect = scrollContainer.getBoundingClientRect();

    // Build node map for quick parent lookups
    const nodeMap = buildNodeMap(data);

    // Find the first visible row that's at or below the sticky threshold
    let firstVisibleRowKey: string | null = null;
    let foundRowScrolledPast = false;

    for (const row of Array.from(rows)) {
      const rect = row.getBoundingClientRect();
      const rowTopRelativeToMain = rect.top - mainRect.top;

      // Check if any row has scrolled above the sticky threshold
      if (rowTopRelativeToMain < stickyThreshold) {
        foundRowScrolledPast = true;
      }

      // Find the first row that starts at or below the sticky threshold
      if (rowTopRelativeToMain >= stickyThreshold - 40) {
        firstVisibleRowKey = row.getAttribute('data-rk');
        break;
      }
    }

    // If no rows have scrolled past, don't show breadcrumb
    if (!foundRowScrolledPast || !firstVisibleRowKey) {
      setBreadcrumbPath([]);
      return;
    }

    // Get the parent chain for the first visible row
    const parentChain = getParentChain(firstVisibleRowKey, nodeMap);

    // Filter to only include expanded parents that have scrolled out of view
    const visibleParents = parentChain.filter((item) => {
      if (!expandedKeys[item.key]) return false;

      // Check if this parent row has scrolled above the sticky threshold
      const parentRow = tbody.querySelector(`tr[data-rk="${item.key}"]`);
      if (!parentRow) return false;

      const parentRect = parentRow.getBoundingClientRect();
      const parentTopRelativeToMain = parentRect.top - mainRect.top;

      return parentTopRelativeToMain < stickyThreshold;
    });

    setBreadcrumbPath(visibleParents);
  }, [tableRef, data, expandedKeys]);

  useEffect(() => {
    const scrollContainer = document.getElementById('main');
    if (!scrollContainer) return;

    // Calculate on mount
    calculateBreadcrumb();

    // Add scroll listener with throttling for performance
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          calculateBreadcrumb();
          ticking = false;
        });
        ticking = true;
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [calculateBreadcrumb]);

  // Recalculate when expanded keys change
  useEffect(() => {
    calculateBreadcrumb();
  }, [expandedKeys, calculateBreadcrumb]);

  return breadcrumbPath;
};
